import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useReveal } from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = ({ prefillProject, onClearPrefill }) => {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    budget: "",
    project_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefillProject) {
      setForm((f) => ({
        ...f,
        project_interest: prefillProject.name,
        message:
          f.message ||
          `I'm interested in ${prefillProject.name} (${prefillProject.developer}). Please share full details, floor plans and payment plan.`,
      }));
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [prefillProject]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry sent. Our investment advisor will contact you within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        budget: "",
        project_interest: "",
        message: "",
      });
      onClearPrefill?.();
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send inquiry. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send inquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 px-6 md:px-12"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1582650625119-3a31f8fa2699?auto=format&fit=crop&w=1920&q=80')",
        }}
      />
      <div className="absolute inset-0 bg-[#0a1128]/85" />

      <div className="relative max-w-5xl mx-auto">
        <div ref={ref} className="reveal glass-strong rounded-3xl p-8 md:p-14">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.5em] text-[#c5a059] uppercase mb-4">
              03 · Private Consultation
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-tight">
              Secure your <span className="italic gold-gradient">golden opportunity</span>
            </h2>
            <p className="text-white/55 mt-4 max-w-xl mx-auto text-sm leading-relaxed">
              Share a few details and our senior investment advisor will reach out with a
              personalised shortlist and payment plan.
            </p>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-testid="inquiry-form">
            <input
              required
              type="text"
              placeholder="Full Name *"
              value={form.name}
              onChange={update("name")}
              className="udp-input"
              data-testid="form-input-name"
            />
            <input
              required
              type="email"
              placeholder="Email Address *"
              value={form.email}
              onChange={update("email")}
              className="udp-input"
              data-testid="form-input-email"
            />
            <div className="phone-input-wrapper" data-testid="form-phone-wrapper">
              <PhoneInput
                country={"ae"}
                value={form.phone}
                onChange={(val) => setForm((f) => ({ ...f, phone: val ? `+${val}` : "" }))}
                enableSearch
                disableSearchIcon={false}
                searchPlaceholder="Search country"
                placeholder="Phone Number *"
                inputProps={{
                  name: "phone",
                  required: true,
                  "data-testid": "form-input-phone",
                }}
                specialLabel=""
              />
            </div>
            <select
              value={form.budget}
              onChange={update("budget")}
              className="udp-input"
              data-testid="form-input-budget"
            >
              <option value="">Budget Range</option>
              <option value="AED 1M - 3M">AED 1M – 3M</option>
              <option value="AED 3M - 10M">AED 3M – 10M</option>
              <option value="AED 10M+">AED 10M+</option>
            </select>
            <input
              type="text"
              placeholder="Project of interest (optional)"
              value={form.project_interest}
              onChange={update("project_interest")}
              className="udp-input md:col-span-2"
              data-testid="form-input-project"
            />
            <textarea
              placeholder="Investment interests / questions"
              value={form.message}
              onChange={update("message")}
              className="udp-input md:col-span-2 min-h-[110px] resize-none"
              data-testid="form-input-message"
            />

            <button
              type="submit"
              disabled={loading}
              className="md:col-span-2 btn-premium py-5 rounded-lg text-sm font-bold mt-6 tracking-[0.3em] flex items-center justify-center gap-3 disabled:opacity-70"
              data-testid="form-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} /> SENDING…
                </>
              ) : (
                <>
                  SEND INQUIRY <Send size={16} />
                </>
              )}
            </button>
            <p className="md:col-span-2 text-[10px] tracking-[0.2em] text-white/40 text-center uppercase mt-2">
              Your information is confidential and used solely for property consultation.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
